/* Look at a thumbnail properly, without leaving the table.
 *
 * The thumbnails in the results tables are 40x40. That is the right size for a
 * list of twenty pages and useless for answering "is this the page I want?" --
 * so hovering one shows it large, over everything else, and moving away puts
 * it back. On a tablet there is no hover, so a press and hold does the same
 * thing.
 *
 * Not on phones. A phone screen is barely bigger than the preview would be,
 * every touch there is already a press, and the same gesture is how you scroll
 * a long table.
 *
 * Written as a directive rather than five handlers repeated at three
 * thumbnails:
 *
 *     <img v-peek="thumbFor(item)" ...>
 *
 * and one <ThumbPeek /> mounted once, which draws whatever the state says.
 * The state is module-level rather than in the Pinia store because it is
 * nothing but this frame's hover: it is not worth persisting, watching, or
 * putting in a snapshot.
 */
import { reactive } from "vue"

// How long a finger has to stay down before this counts as "hold" rather than
// "tap" or "the start of a scroll". 400ms is long enough that scrolling a long
// table never triggers it and short enough not to feel broken.
const HOLD_MS = 400

// How far a finger may move and still count as holding still.
const HOLD_SLOP = 12

export const peek = reactive({
    // The image being shown, or "" for nothing.
    src: "",
    // Where the thumbnail is, so the preview can sit beside it rather than in
    // the middle of the screen: {top, bottom, left, right}, viewport
    // coordinates. The component does the clamping.
    anchor: null,
    // Whether this device gets previews at all, and by which gesture.
    hover: false,
    hold: false,
})

export function configurePeek({hover = false, hold = false} = {}) {
    peek.hover = hover
    peek.hold = hold
    if (!hover && !hold) {
        hide()
    }
}

// The thumbnail currently being looked at, so a scroll can re-measure it
// rather than give up on it.
let current = null

function anchorOf(el) {
    const r = el.getBoundingClientRect()
    return {top: r.top, bottom: r.bottom, left: r.left, right: r.right}
}

function show(el, src) {
    if (!src) return
    current = el
    peek.anchor = anchorOf(el)
    peek.src = src
}

export function hide() {
    current = null
    peek.src = ""
    peek.anchor = null
}

/* Follow the thumbnail when the page moves under it.
 *
 * This used to be hide(): scrolling moves the thumbnail out from under the
 * pointer without a reliable mouseleave in every browser, so a scroll gave up
 * on the preview. Measured, and it is the wrong trade -- a lazy image arriving
 * and an ad slot claiming its height both scroll the page a few pixels on
 * their own, so the preview appeared correctly and then vanished a moment
 * later with nobody touching anything. Three stray scroll events, none of them
 * the visitor's.
 *
 * So a scroll re-measures instead, and only a thumbnail that has actually left
 * the viewport puts the preview away.
 */
export function reanchor() {
    if (!current || !peek.src) return
    if (!current.isConnected) {
        hide()
        return
    }
    const r = current.getBoundingClientRect()
    const h = window.innerHeight || 0
    const w = window.innerWidth || 0
    if (r.bottom < 0 || r.top > h || r.right < 0 || r.left > w) {
        hide()
        return
    }
    peek.anchor = anchorOf(current)
}

export const vPeek = {
    mounted(el, binding) {
        const state = {timer: null, from: null, held: false}

        const cancelHold = () => {
            if (state.timer) {
                clearTimeout(state.timer)
                state.timer = null
            }
        }

        el._peek = {
            enter() {
                if (peek.hover) show(el, binding.value)
            },
            leave() {
                if (peek.hover) hide()
            },
            touchstart(event) {
                if (!peek.hold) return
                const t = event.touches && event.touches[0]
                state.from = t ? {x: t.clientX, y: t.clientY} : null
                state.held = false
                cancelHold()
                state.timer = setTimeout(() => {
                    state.held = true
                    show(el, binding.value)
                }, HOLD_MS)
            },
            touchmove(event) {
                if (!state.from) return
                const t = event.touches && event.touches[0]
                if (!t) return
                // A finger that has travelled is scrolling, not holding.
                if (Math.abs(t.clientX - state.from.x) > HOLD_SLOP
                        || Math.abs(t.clientY - state.from.y) > HOLD_SLOP) {
                    cancelHold()
                    if (state.held) hide()
                    state.held = false
                    state.from = null
                }
            },
            touchend(event) {
                cancelHold()
                state.from = null
                if (state.held) {
                    // The hold showed the preview, so the tap that ends it must
                    // not also follow the link the thumbnail sits in.
                    event.preventDefault()
                    hide()
                }
                state.held = false
            },
            contextmenu(event) {
                // A long press on an image is "save image" on a tablet, which
                // is not what was asked for and cannot be undone once the sheet
                // is up. Only suppressed where the hold gesture is live.
                if (peek.hold) event.preventDefault()
            },
        }

        el.addEventListener("mouseenter", el._peek.enter)
        el.addEventListener("mouseleave", el._peek.leave)
        el.addEventListener("touchstart", el._peek.touchstart, {passive: true})
        el.addEventListener("touchmove", el._peek.touchmove, {passive: true})
        // NOT passive: touchend calls preventDefault to swallow the click that
        // ended a hold.
        el.addEventListener("touchend", el._peek.touchend)
        el.addEventListener("touchcancel", el._peek.touchend)
        el.addEventListener("contextmenu", el._peek.contextmenu)
    },

    updated(el, binding) {
        // The row's image can change under a hover -- a variant finishing, a
        // re-optimise -- and the preview should follow it rather than keep
        // showing the blob that has just been replaced.
        if (peek.src && binding.value && binding.oldValue === peek.src) {
            peek.src = binding.value
        }
    },

    unmounted(el) {
        if (!el._peek) return
        el.removeEventListener("mouseenter", el._peek.enter)
        el.removeEventListener("mouseleave", el._peek.leave)
        el.removeEventListener("touchstart", el._peek.touchstart)
        el.removeEventListener("touchmove", el._peek.touchmove)
        el.removeEventListener("touchend", el._peek.touchend)
        el.removeEventListener("touchcancel", el._peek.touchend)
        el.removeEventListener("contextmenu", el._peek.contextmenu)
        delete el._peek
        // A row that goes away while its preview is up would leave the preview
        // there for ever.
        hide()
    },
}
